import { InitialData, Payload, TFormValues } from '../CalculationId.types';
import { FIELDS } from '../partials/Form/Form.types';

type Applicant = NonNullable<TFormValues['applicant1' | 'applicant2']>;
type ApiPayloadValue = NonNullable<Payload['applicant1' | 'applicant2']>;
type ApiInitialValue = NonNullable<InitialData['loanData']['applicant1' | 'applicant2']>;

type PensionsUtil<FormValues, ApiInitialValues, ApiPayloadValue> = {
  create: () => FormValues;
  mapForm: (data: FormValues) => ApiPayloadValue;
  mapApi: (data: ApiInitialValues) => FormValues;
};

export const currentPensions: PensionsUtil<
  Applicant[FIELDS.CURRENT_PENSION_PREFIX],
  ApiInitialValue['currentPensions'],
  ApiPayloadValue['currentPensions']
> = {
  create: () => {
    return {
      [FIELDS.STATE_PENSION]: 0,
      [FIELDS.DEFINED_BENEFIT]: 0,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT]: 0,
      [FIELDS.DEFINED_CONTRIBUTION]: 0,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION]: 0,
      [FIELDS.ANNUITY]: 0,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY]: 0,
    };
  },

  mapForm: (data) => {
    return {
      statePension: data[FIELDS.STATE_PENSION] !== null ? Number(data[FIELDS.STATE_PENSION]) : null,
      definedBenefit: data[FIELDS.DEFINED_BENEFIT] as number,
      dbPercentToPartnerOnDeath: data[FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT] as number,
      definedContribution: data[FIELDS.DEFINED_CONTRIBUTION] as number,
      dcPercentToPartnerOnDeath: data[FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION] as number,
      annuity: data[FIELDS.ANNUITY] as number,
      annuityPercentToPartnerOnDeath: data[FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY] as number,
    };
  },

  mapApi: (data): Applicant[FIELDS.CURRENT_PENSION_PREFIX] => {
    return {
      [FIELDS.STATE_PENSION]: data.statePension,
      [FIELDS.DEFINED_BENEFIT]: data.definedBenefit,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT]: data.dbPercentToPartnerOnDeath,
      [FIELDS.DEFINED_CONTRIBUTION]: data.definedContribution,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION]: data.dcPercentToPartnerOnDeath,
      [FIELDS.ANNUITY]: data.annuity,
      [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY]: data.annuityPercentToPartnerOnDeath,
    };
  },
};

export const futurePensions: PensionsUtil<
  Applicant[FIELDS.FUTURE_PENSION_PREFIX],
  ApiInitialValue['futurePensions'],
  ApiPayloadValue['futurePensions']
> = {
  create: () => {
    return {
      [FIELDS.STATE_PENSION]: 0,
      [FIELDS.DEFINED_BENEFIT]: 0,
      [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT]: 0,
      [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT]: 0,
      [FIELDS.USE_OF_FUNDS]: null,
      [FIELDS.DEFINED_CONTRIBUTION]: 0,
      [FIELDS.ONGOING_CONTRIBUTIONS]: 0,
      [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION]: 0,
      [FIELDS.LUMP_SUM_DRAWDOWN]: 0,
      [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION]: 0,
    };
  },
  mapForm: (data): ApiPayloadValue['futurePensions'] => {
    const definedContribution = data[FIELDS.DEFINED_CONTRIBUTION] as number;

    return {
      statePension: data[FIELDS.STATE_PENSION] !== null ? Number(data[FIELDS.STATE_PENSION]) : null,
      definedBenefit: data[FIELDS.DEFINED_BENEFIT] as number,
      dbPercentToPartnerOnDeath: data[FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT] as number,
      dbLumpSumUsedToRepayLoan: data[FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT] as number,
      definedContribution,
      useOfFunds: (data[FIELDS.DEFINED_CONTRIBUTION] as number) > 0 ? data[FIELDS.USE_OF_FUNDS] : null,
      ongoingContributions: data[FIELDS.ONGOING_CONTRIBUTIONS] as number,
      dcAnnuityPercentToPartnerOnDeath: data[FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION] as number,
      percentLumpSumDrawdown: data[FIELDS.LUMP_SUM_DRAWDOWN] as number,
      dcLumpSumPercentUsedToRepayLoan: data[FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION] as number,
    };
  },
  mapApi: (data) => {
    return {
      [FIELDS.STATE_PENSION]: data.statePension,
      [FIELDS.DEFINED_BENEFIT]: data.definedBenefit,
      [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT]: data.dbPercentToPartnerOnDeath,
      [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT]: data.dbLumpSumUsedToRepayLoan,
      [FIELDS.USE_OF_FUNDS]: data.useOfFunds,
      [FIELDS.DEFINED_CONTRIBUTION]: data.definedContribution,
      [FIELDS.ONGOING_CONTRIBUTIONS]: data.ongoingContributions,
      [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION]: data.dcAnnuityPercentToPartnerOnDeath,
      [FIELDS.LUMP_SUM_DRAWDOWN]: data.percentLumpSumDrawdown,
      [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION]: data.dcLumpSumPercentUsedToRepayLoan,
    };
  },
};
